import { useEffect, useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import Button from "./Button";
import { useTranslation } from "react-i18next";
import { axiosInstance } from "../api/api";
import Loading from "./Loading";
import dayjs from "dayjs";
import "dayjs/locale/en";
import isBetween from "dayjs/plugin/isBetween";
import useTheme from "../hooks/useTheme";
import { useCurrencyFormatter } from "../hooks/useCurrencyFormatter";
import ConditionStatementButton from "./Modals/ConditionStatementButton";
import { useHistory } from 'react-router-dom';

dayjs.locale("en");
dayjs.extend(isBetween);

function PipelineDeals({ searchData, apiEndpoint, pageTitle, initialData }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme() || {};
  const history = useHistory();
  const formatCurrency = useCurrencyFormatter();

  // Store original (untranslated) data
  const originalDataRef = useRef(initialData || []);
  
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;
  const [loading, setLoading] = useState(false);

  // Define fields that need translation
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fieldsToTranslate = [
    'loanStatus',
    'loanSubStatus',
    'loanPurpose',
    'loanSubPurpose',
    'portalStatus',
    'lob'
  ];

  // Function to translate a single field
  const translateField = useCallback((fieldType, value) => {
    if (!value) return '';
    
    const translationKey = `apiTranslations.${fieldType}.${value}`;
    
    // Check if translation exists in i18next
    const translatedValue = t(translationKey);
    
    // If the translation key was returned unchanged, it means no translation exists
    return translationKey === translatedValue ? value : translatedValue;
  }, [t]);

  // Function to translate data
  const translateData = useCallback((originalData) => {
    if (!originalData || !Array.isArray(originalData)) return [];
    
    return originalData.map(item => {
      const translatedItem = { ...item };
      
      fieldsToTranslate.forEach(field => {
        if (translatedItem[field]) {
          translatedItem[field] = translateField(field, translatedItem[field]);
        }
      });
      
      return translatedItem;
    });
  }, [fieldsToTranslate, translateField]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (initialData) {
      originalDataRef.current = initialData;
      return;
    }
    
    try {
      setLoading(true);
      const response = await axiosInstance.get(`${apiEndpoint}?page=0&pageSize=1000`);

      if (response.data) {
        originalDataRef.current = response.data;
      } else {
        console.error("Failed to fetch data for PipelineDeals.");
        originalDataRef.current = [];
      }
    } catch (error) {
      console.error("Error fetching data for PipelineDeals:", error.message);
      if (error.response?.status === 401) {
        window.location.href = '/';
      }
      originalDataRef.current = [];
    } finally {
      setLoading(false);
    }
  }, [apiEndpoint, initialData]);

  // Apply translations whenever language changes
  useEffect(() => {
    const translatedData = translateData(originalDataRef.current);
    setData(translatedData);
  }, [i18n.language, translateData]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply filters when data or search criteria change
  useEffect(() => {
    applyFiltersAndSort(data, searchData);
  }, [data, searchData]);

  const applyFiltersAndSort = (apiData, filters) => {
    if (!filters || Object.values(filters).every(value => value === "")) {
      setFilteredData(apiData);
      return;
    }

    let updatedData = apiData;

    // Filter by Mortgage Account Number
    if (filters.mortgageAccountNumber) {
      updatedData = updatedData.filter(item => 
        item.mortgageAccountNumber && item.mortgageAccountNumber.toLowerCase().includes(filters.mortgageAccountNumber.toLowerCase())
      );
    }

    // Filter by Broker Account ID (poS_System_ID)
    if (filters.poS_System_ID) {
      updatedData = updatedData.filter(item => 
        item.poS_System_ID && item.poS_System_ID.toLowerCase().includes(filters.poS_System_ID.toLowerCase())
      );
    }

    // Filter by LOB
    if (filters.lob) {
      updatedData = updatedData.filter(item => 
        item.lob && item.lob.toLowerCase() === filters.lob.toLowerCase()
      );
    }

    // Filter by Primary Borrower Name (first name + last name)
    if (filters.primaryBorrowerName) {
      const searchName = filters.primaryBorrowerName.toLowerCase();
      updatedData = updatedData.filter(item => {
        const fullName = `${item.primaryBorrowerFirstName || ''} ${item.primaryBorrowerLastName || ''}`.toLowerCase();
        return fullName.includes(searchName) || 
              (item.primaryBorrowerFirstName && item.primaryBorrowerFirstName.toLowerCase().includes(searchName)) || 
              (item.primaryBorrowerLastName && item.primaryBorrowerLastName.toLowerCase().includes(searchName));
      });
    }

    // Filter by Property Address
    if (filters.propertyAddress) {
      updatedData = updatedData.filter(item => 
        item.propertyAddress && item.propertyAddress.toLowerCase().includes(filters.propertyAddress.toLowerCase())
      );
    }

    // Filter by Application Date Range
    if (filters.applicationDateFrom || filters.applicationDateTo) {
      updatedData = updatedData.filter(item => {
        const itemDate = dayjs(item.applicationDate);
        const fromDate = filters.applicationDateFrom ? dayjs(filters.applicationDateFrom) : null;
        const toDate = filters.applicationDateTo ? dayjs(filters.applicationDateTo).endOf('day') : null;

        if (fromDate && toDate) {
          return itemDate.isBetween(fromDate, toDate, null, '[]');
        } else if (fromDate) {
          return itemDate.isAfter(fromDate) || itemDate.isSame(fromDate);
        } else if (toDate) {
          return itemDate.isBefore(toDate) || itemDate.isSame(toDate);
        }
        return true;
      });
    }

    // Filter by Closing Date Range
    if (filters.closingDateFrom || filters.closingDateTo) {
      updatedData = updatedData.filter(item => {
        const itemDate = dayjs(item.closingDate);
        const fromDate = filters.closingDateFrom ? dayjs(filters.closingDateFrom) : null;
        const toDate = filters.closingDateTo ? dayjs(filters.closingDateTo).endOf('day') : null;

        if (fromDate && toDate) {
          return itemDate.isBetween(fromDate, toDate, null, '[]');
        } else if (fromDate) {
          return itemDate.isAfter(fromDate) || itemDate.isSame(fromDate);
        } else if (toDate) {
          return itemDate.isBefore(toDate) || itemDate.isSame(toDate);
        }
        return true;
      });
    }

    setFilteredData(updatedData);
  };

  const handleTableBtn = (mortgageAccountNumber) => {
    // Get the current full path including search parameters
    const currentPath = window.location.pathname + window.location.search;
    
    // When navigating to detail view, explicitly store the return path
    history.push({
      pathname: `/dview/${mortgageAccountNumber}`,
      search: `?lang=${i18n.language}`,
      state: { returnTo: currentPath }
    });
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredData.slice(indexOfFirstRow, indexOfLastRow);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1);

  return (
    <div className="w-full h-fit p-5">
      <div className="flex flex-col h-full gap-2">
        <span className="labels text-xl font-semibold">
          {t(pageTitle)}
        </span>
        {!loading ? (
          <div className="rounded-md h-full border-2 p-5 overflow-auto">
            <table className="w-full">
              <thead>
                <tr className="labels">
                  {[
                    "Broker",
                    "Mortgage Account Number",
                    "Broker Account ID",
                    "LOB",
                    "Primary Borrower",
                    "Loan Purpose",
                    "Property address",
                    "Status",
                    "Closing Date",
                    "Application Date",
                    "Amount",
                    "Outstanding Conditions",
                    "Actions"
                  ].map((header) => (
                    <th key={header} className={`labels border p-2 text-${theme.labelColor || 'black'}`}>
                      {t(`dealsSearch.${header}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-opacity-30 transition-colors duration-200" style={{backgroundColor: theme.backgroundColor}}>
                    <td className="border p-2">{`${row.brokerAgentFirstName} ${row.brokerAgentLastName}`}</td>
                    <td className="border p-2">
                      <span
                        onClick={() => handleTableBtn(row.mortgageAccountNumber)}
                        className="text-center p-2 rounded-full cursor-pointer hover:bg-zinc-200 border-b-black bg-zinc-100"
                      >
                        {row.mortgageAccountNumber}
                      </span>
                    </td>
                    <td className="border p-2">{row.poS_System_ID}</td>
                    <td className="border p-2">{row.lob}</td>
                    <td className="border p-2">{`${row.primaryBorrowerFirstName} ${row.primaryBorrowerLastName}`}</td>
                    <td className="border p-2">{row.loanPurpose}</td>
                    <td className="border p-2">{row.propertyAddress}</td>
                    <td className="border p-2">{row.portalStatus}</td>
                    <td className="border p-2">{dayjs(row.closingDate).format('YYYY-MM-DD')}</td>
                    <td className="border p-2">{dayjs(row.applicationDate).format('YYYY-MM-DD')}</td>
                    <td className="border p-2">{formatCurrency(row.totalLoanAmount)}</td>
                    <td className={`border p-2 ${row.outstandingConditions === 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {row.outstandingConditions}
                    </td>
                    <td
                      className="border px-5 w-full h-full p-2 flex gap-2 items-center justify-around"
                    >
                      <div className="flex flex-row gap-2">
                        <div>
                          <Button accountID={row.mortgageAccountNumber} variant="docUpload" />
                        </div>
                        <ConditionStatementButton accountID={row.mortgageAccountNumber} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-center mt-4">
              {pageNumbers.map((pageNumber) => (
                <button
                  key={pageNumber}
                  onClick={() => paginate(pageNumber)}
                  className={`mx-1 p-2 border rounded-full transition-colors duration-200`}
                  style={{
                    backgroundColor: currentPage === pageNumber ? theme.primaryButtonBgColor : 'white',
                    color: currentPage === pageNumber ? theme.primaryButtonTextColor : theme.textColor,
                    borderColor: theme.primaryButtonBgColor,
                  }}
                >
                  {pageNumber}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <Loading />
        )}
      </div>
    </div>
  );
}

PipelineDeals.propTypes = {
  searchData: PropTypes.object,
  apiEndpoint: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
  initialData: PropTypes.array,
};

export default PipelineDeals;